/**
 * Edit-mode diff for the family day-order confirmation dialog.
 *
 * In EDIT mode (a placed, still-editable order) every staged quantity change —
 * whether it ADDS to, removes, or adjusts an already-ordered cell — must be
 * confirmed before commit. `diffOrderChanges` keeps only the cells whose
 * quantity actually moved (`oldQty !== newQty`), preserving input order.
 */

/** A single before→after quantity change for one (item, child, mealtime) cell. */
export interface OrderChange {
  key: string
  itemName: string
  childName: string
  mealtimeLabel: string
  oldQty: number
  newQty: number
}

/** Keep only the cells whose quantity changed — additions, removals, and adjustments alike. */
export function diffOrderChanges(cells: ReadonlyArray<OrderChange>): OrderChange[] {
  return cells.filter((cell) => cell.oldQty !== cell.newQty)
}

/** One (item, child, mealtime) cell priced for the edit-footer delta. */
export interface OrderDeltaCell {
  oldQty: number
  newQty: number
  /** Browse (menu) unit price — what an ADDED unit will cost, pre-subsidy. */
  browseUnitPriceCents: number
  /**
   * What the parent actually PAID for the placed cell, as a line total net of
   * subsidy (a fully covered meal is 0). Null when the cell wasn't ordered.
   */
  paidLineCents: number | null
}

/**
 * Signed NET money change of an edit draft vs the placed order, in cents.
 *
 * Additions are estimated at the browse price; removals refund the per-unit
 * price actually paid (net of subsidy — removing a covered meal is −0, which
 * is what the server's replace settles). The server recomputes
 * authoritatively on save; this is the live footer estimate.
 */
export function editDeltaCents(cells: ReadonlyArray<OrderDeltaCell>): { deltaCents: number; changedCells: number } {
  let deltaCents = 0
  let changedCells = 0
  for (const cell of cells) {
    const diff = cell.newQty - cell.oldQty
    if (diff === 0) {
      continue
    }
    changedCells += 1
    if (diff > 0) {
      deltaCents += diff * cell.browseUnitPriceCents
    } else {
      const perUnitPaid =
        cell.paidLineCents !== null && cell.oldQty > 0 ? cell.paidLineCents / cell.oldQty : cell.browseUnitPriceCents
      deltaCents += diff * perUnitPaid
    }
  }
  return { deltaCents: Math.round(deltaCents), changedCells }
}
